import React, { createContext, useState, useContext, useEffect } from 'react';
import { WorkflowStep } from './WorkflowProgress';
import { useConsumerDetails, useDocument } from '../../hooks';
import { useParams } from 'react-router-dom';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { logEvent } from '@shared/analytics/analytics';
import { formatTerritoryAddress } from '@shared/utils/address';

export type ApplicationStatus = 'pending_review' | 'document_verification' | 'site_inspection' | 'approval' | 'installation' | 'installation_inprogress' | 'installation_completed' | 'activation' | 'completed' | 'rejected';

export interface Application {
  statusDisplay: any;
  id: string;
  consumerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  submissionDate: string;
  serviceAddress?:string;
  billingAddress?: string;
  plan?: string;
  category?: string;
  subCategory?: string;
  isVip?: boolean;
  status: ApplicationStatus;
  apiStatus: string; // Add original API status
  daysRemaining: number;
  totalDays: number;
  progressStatusDisplay: any;
  documents: {
    file: string;
    id: string;
    name: string;
    status: 'verified' | 'pending' | 'rejected';
    uploadDate: string;
    fileUrl:any
  }[];
  notes: {
    id: string;
    text: string;
    createdBy: string;
    createdAt: string;
    noteName: string;
  }[];
  currentStep: number;
  requiresInspection: boolean;
  payment?: {
    id: number;
    transactionId: string | null;
    amount: string;
    paymentMode: string;
    paymentPayType: number;
    paymentPayTypeDisplay: string;
    paymentDate: string;
    status: string;
    createdDate: string;
    paymentModeDisplay: string;
  };
  addressData?: {
    service?: {
      PREMISE?: string;
      AREA?: string;
      SUBAREA?:  string;
    }
  }
  additionalData?: {
    isSiteInspection: boolean;
    kyc: boolean;
    isPaymentApprove: boolean;
    isLegalReqMet: boolean;
  }
}

interface WorkflowContextType {
  application: Application | null;
  workflowSteps: WorkflowStep[];
  consumerId: string | null;
  statusFromUrl: string | null; // Add status from URL
  isLoading: boolean;
  verifyDocument: (docId: string) => void;
  rejectDocument: (docId: string) => void;
  moveToNextStep: () => void;
  skipToStep: (stepId: number) => void;
  addNote: (text: string) => void;
  updateApplicationStatus: (status: ApplicationStatus) => void;
  toggleInspectionRequirement: (required: boolean) => void;
  getStepPath: (stepId: number) => string;
  getCurrentStepFromStatus: (status: string) => number;
  canNavigateToStep: (stepId: number, currentStep: number) => boolean;
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);

// Helper function to determine current step from API status
const getCurrentStepFromStatus = (status: string): number => {
  if (!status) return 1;
  const normalizedStatus = status
    .toUpperCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  switch (normalizedStatus) {
    case "PENDING":
    case "PENDING REVIEW":
    case "APPLICATION REVIEW":
    case "CREATED":
      return 1; // Application Review

    case "ACCEPTED":
    case "SITE INSPECTION":
    case "DOCUMENT VERIFICATION":
    case "INSPECTION":
      return 2; // Site Inspection

    case "APPROVAL":
    case "APPROVAL INPROGRESS":
    case "SURVEY INPROGRESS":
    case "SURVEY COMPLETED":
      return 3; // Approval

    case "APPROVED":
    case "INSTALLATION":
    case "INSTALLATION INPROGRESS":
      return 4; // Installation

    case "INSTALLATION COMPLETED":
    case "ACTIVATION":
    case "ACTIVATION INPROGRESS":
    case "ACTIVATED":
    case "COMPLETED":
      return 5; // Activation

    default:
      if (
        normalizedStatus.includes("ACTIVAT") ||
        normalizedStatus.includes("COMPLETE")
      )
        return 5;
      if (normalizedStatus.includes("INSTALL")) return 4;
      if (
        normalizedStatus.includes("APPROV") ||
        normalizedStatus.includes("SURVEY")
      )
        return 3;
      if (
        normalizedStatus.includes("INSPECT") ||
        normalizedStatus.includes("ACCEPT")
      )
        return 2;
      return 1;
  }
};

// Helper function to determine step status based on current step
const getStepStatus = (
  stepId: number,
  currentStep: number,
): "completed" | "active" | "PENDING" | "ACCEPTED" => {
  if (stepId < currentStep) {
    return "completed";
  } else if (stepId === currentStep) {
    return "active";
  } else {
    return "PENDING";
  }
};

// Helper function to check if user can navigate to a step
const canNavigateToStep = (stepId: number, currentStep: number): boolean => {
  // Allow navigating to current step and any previous or unlocked step
  return true;
};

// Updated workflow steps with consistent parameter naming
const WORKFLOW_STEPS_TEMPLATE: Omit<WorkflowStep, 'path' | 'status'>[] = [
  { id: 1, name: "Application Review" },
  { id: 2, name: "Site Inspection" },
  { id: 3, name: "Approval" },
  { id: 4, name: "Installation" },
  { id: 5, name: "Activation" },
];

// Helper function to generate paths with consumer ID and status
const generateWorkflowSteps = (consumerId: string, statusFromUrl: string, currentStep: number): WorkflowStep[] => {
  const pathTemplates = [
    `/cx/consumer-onboarding/view/${consumerId}/${statusFromUrl}`,
    `/cx/consumer-onboarding/workflow/inspection/${consumerId}/${statusFromUrl}`,
    `/cx/consumer-onboarding/workflow/approval/${consumerId}/${statusFromUrl}`,
    `/cx/consumer-onboarding/workflow/installation/${consumerId}/${statusFromUrl}`,
    `/cx/consumer-onboarding/workflow/activation/${consumerId}/${statusFromUrl}`,
  ];

  return WORKFLOW_STEPS_TEMPLATE.map((step, index) => ({
    ...step,
    path: pathTemplates[index],
    status: getStepStatus(step.id, currentStep)
  }));
};

// Helper function to map API status to ApplicationStatus
const mapApiStatusToApplicationStatus = (status: string): ApplicationStatus => {
  const statusMap: Record<string, ApplicationStatus> = {
    'PENDING': 'pending_review',
    'PENDING_REVIEW': 'pending_review',
    'ACCEPTED': 'site_inspection',
    'DOCUMENT_VERIFICATION': 'document_verification',
    'SITE_INSPECTION': 'site_inspection',
    'SURVEY INPROGRESS': 'approval',
    'SURVEY COMPLETED': 'approval',
    'APPROVAL': 'approval',
    'APPROVED': 'approval',
    'INSTALLATION': 'installation',
    'INSTALLATION INPROGRESS': 'installation_inprogress',
    'INSTALLATION COMPLETED': 'installation_completed',
    'ACTIVATION': 'activation',
    'COMPLETED': 'completed',
    'REJECTED': 'rejected'
  };
  return statusMap[status] || 'pending_review';
};

// Helper function to calculate days remaining
const calculateDaysRemaining = (createdDate: string): { daysRemaining: number; totalDays: number } => {
  const created = new Date(createdDate);
  const now = new Date();
  const totalDays = 7; // Standard processing time
  const daysPassed = Math.floor((now.getTime() - created.getTime()) / (1000 * 3600 * 24));
  const daysRemaining = Math.max(0, totalDays - daysPassed);
  
  return { daysRemaining, totalDays };
};

export const WorkflowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const remoteUtilityId = getRemoteUtilityId();
  const [application, setApplication] = useState<Application | null>(null);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  
  // Extract consumer ID and status from URL params
  const params = useParams<{ id?: string; consumerId?: string; status?: string }>();
  const consumerId = params.consumerId || params.id || null;
  const statusFromUrl = params.status || null;
  
  // Fetch consumer details
  const { data: consumerHistoryData, isLoading: isConsumerHistoryLoading } = useConsumerDetails({
    remote_utility_id: String(remoteUtilityId),
    consumer_id: consumerId,
    is_detail_info: true,
  });

  // Fetch documents separately
  const { data: documentsData, isLoading: isDocumentsLoading } = useDocument({
    remote_utility_id: remoteUtilityId,
    consumer_id: consumerId
  });

  const isLoading = isConsumerHistoryLoading || isDocumentsLoading;

  // Transform API data into Application format
  useEffect(() => {
    if (consumerHistoryData?.result && consumerId) {
      const apiData = consumerHistoryData.result;
      console.log('apiData', apiData);
      const { daysRemaining, totalDays } = calculateDaysRemaining(apiData.createdDate);

      // Use status from URL or fall back to API status
      const currentStatus = statusFromUrl || apiData.statusDisplay;
      const currentStep = getCurrentStepFromStatus(currentStatus);

      // Create customer name from first_name and last_name
      const customerName = `${apiData.firstName} ${apiData.lastName}`.trim();
      
      // Transform documents data if available
      const documents = documentsData?.result?.map((doc: any, index: number) => ({
        id: doc.id || `doc${index + 1}`,
        name: doc.documentType || doc.name || `Document ${index + 1}`,
        status: doc.status === 'verified' ? 'verified' : doc.status === 'rejected' ? 'rejected' : 'pending',
        uploadDate: doc.uploadDate || doc.createdDate || apiData.createdDate,
        fileUrl: doc.file
      })) || [];

      // Transform payment data from API response
      const paymentData = apiData.payment ? {
        id: apiData.payment.id,
        transactionId: apiData.payment.transaction_id,
        amount: apiData.payment.amount,
        paymentMode: apiData.payment.paymentMode,
        paymentPayType: apiData.payment.payment_pay_type,
        paymentPayTypeDisplay: apiData.payment.paymentPayTypeDisplay,
        paymentDate: apiData.payment.paymentDate,
        status: apiData.payment.status,
        createdDate: apiData.payment.createdDate,
        paymentModeDisplay: apiData.payment.paymentModeDisplay
      } : undefined;

      const transformedApplication: Application = {
        id: apiData.consumerNo || `APP-${apiData.id}`,
        consumerId,
        customerName,
        customerEmail: apiData.email,
        customerPhone: apiData.contactNumber,
        submissionDate: apiData.createdDate,
        // serviceAddress: apiData.addressMap || "Address not provided",
        // billingAddress: apiData.billingAddress,
        serviceAddress: formatTerritoryAddress(apiData?.territoryData, "service") || "Address not provided",
        billingAddress: formatTerritoryAddress(apiData?.territoryData, "billing") || "Address not provided",
        plan: `Plan ID: ${apiData.remotePlanId}`,
        category: apiData.ownershipDisplay || "Individual",
        subCategory: apiData.sourceDisplay || "Branch",
        isVip: apiData.isVip || false,
        statusDisplay: apiData.statusDisplay,
        progressStatusDisplay: apiData.progressStatusDisplay,
        status: mapApiStatusToApplicationStatus(apiData.statusDisplay),
        apiStatus: currentStatus, // Store original API status
        daysRemaining,
        totalDays,
        documents,
        addressData: {
          service: {
            PREMISE: apiData.addressData?.service?.PREMISE,
            AREA: apiData.addressData?.service?.AREA,
            SUBAREA: apiData.addressData?.service?.SUBAREA,
          }
        },
        additionalData: {
          isSiteInspection: apiData.additionalData?.isSiteInspection,
          kyc: apiData.additionalData?.kyc,
          isPaymentApprove: apiData.additionalData?.isPaymentApprove,
          isLegalReqMet: apiData.additionalData?.isLegalReqMet
        },
        notes: [
          {
            id: "note1",
            text: `Application created by ${apiData.createdUserRemoteName}`,
            createdBy: apiData.createdUserRemoteName || "System",
            createdAt: apiData.createdDate,
            noteName: "Application created"
          }
        ],
        currentStep,
        requiresInspection: true,
        payment: paymentData
      };

      setApplication(transformedApplication);

      // Generate workflow steps with updated current step
      if (statusFromUrl) {
        setWorkflowSteps(generateWorkflowSteps(consumerId, statusFromUrl, currentStep));
      }
    }
  }, [consumerHistoryData, documentsData, consumerId, statusFromUrl]);

  // Helper function to get step path with consumer ID and status
  const getStepPath = (stepId: number): string => {
    const step = workflowSteps.find(s => s.id === stepId);
    return step?.path || '#';
  };

  const verifyDocument = (docId: string) => {
    setApplication(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        documents: prev.documents.map(doc => 
          doc.id === docId ? { ...doc, status: 'verified' } : doc
        )
      };
    });
    logEvent('Consumer Application Review Document Verified', {
      docId,
    })
  };

  const rejectDocument = (docId: string) => {
    setApplication(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        documents: prev.documents.map(doc => 
          doc.id === docId ? { ...doc, status: 'rejected' } : doc
        )
      };
    });
    logEvent('Consumer Application Review Document Rejected', {
      docId,
    })
  };

  const moveToNextStep = () => {
    setApplication(prev => {
      if (!prev) return prev;
      
      let nextStep = prev.currentStep + 1;
      if (prev.currentStep === 1 && !prev.requiresInspection) {
        nextStep = 3; // Skip inspection step
      }
      
      return {
        ...prev,
        currentStep: nextStep
      };
    });

    // Update workflow steps
    if (application && consumerId && statusFromUrl) {
      const newCurrentStep = application.currentStep + 1;
      setWorkflowSteps(generateWorkflowSteps(consumerId, statusFromUrl, newCurrentStep));
    }
  };
  
  const skipToStep = (stepId: number) => {
    setApplication(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        currentStep: stepId
      };
    });

    // Update workflow steps
    if (consumerId && statusFromUrl) {
      setWorkflowSteps(generateWorkflowSteps(consumerId, statusFromUrl, stepId));
    }
  };

  const addNote = (text: string) => {
    setApplication(prev => {
      if (!prev) return prev;
      const newNote = {
        id: `note${prev.notes.length + 1}`,
        text,
        createdBy: consumerHistoryData?.result?.lastModifiedUserRemoteName || "Current User",
        createdAt: new Date().toLocaleString(),
        noteName: "New Note"
      };
      return {
        ...prev,
        notes: [...prev.notes, newNote]
      };
    });
  };

  const updateApplicationStatus = (status: ApplicationStatus) => {
    setApplication(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        status
      };
    });
  };
  
  const toggleInspectionRequirement = (required: boolean) => {
    setApplication(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        requiresInspection: required
      };
    });
  };

  return (
    <WorkflowContext.Provider 
      value={{ 
        application, 
        workflowSteps,
        consumerId,
        statusFromUrl,
        isLoading,
        verifyDocument, 
        rejectDocument, 
        moveToNextStep, 
        skipToStep,
        addNote,
        updateApplicationStatus,
        toggleInspectionRequirement,
        getStepPath,
        getCurrentStepFromStatus,
        canNavigateToStep
      }}
    >
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};